import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import AdminShell     from '../layouts/shells/AdminShell';
import StaffShell     from '../layouts/shells/StaffShell';
import CommunityShell from '../layouts/shells/CommunityShell';
import UserShell      from '../layouts/shells/UserShell';
import GenpopShell    from '../layouts/shells/GenpopShell';
import ProponentShell from '../layouts/shells/ProponentShell';

// Valid panel values (matches backend PANELS constant and accessControl.js)
export const PANELS = {
  ADMIN:     'admin',
  STAFF:     'staff',
  PROPONENT: 'proponent',
  COMMUNITY: 'community',
  USER:      'user',
  GENPOP:    'genpop',
};

// Shell map: each panel value resolves to a layout component.
// Each shell renders <Navbar /> + panel-specific sidenav + <Outlet />.
// Pages no longer wrap themselves in <MainLayout> — the shell owns the chrome.
const SHELL_MAP = {
  admin:     AdminShell,
  staff:     StaffShell,
  proponent: ProponentShell,
  community: CommunityShell,
  user:      UserShell,
  genpop:    GenpopShell,
};

/**
 * PanelRouter — the single layout route that wraps all authenticated paths.
 *
 * Redirects to /login when unauthenticated.
 * Dispatches to the correct panel shell once the session is loaded.
 * The shell renders <Outlet /> so all child routes flow through it.
 */
const PanelRouter = () => {
  const { user, panel, loading } = useAuth();

  if (loading) {
    return (
      <div className="panel-loading" aria-busy="true">
        <span>Loading…</span>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  const Shell = SHELL_MAP[panel] || GenpopShell;
  return <Shell />;
};

/**
 * PanelRoute — per-route access guard.
 *
 * Wrap a page element with this when the route should only be accessible
 * from specific panels or globalRoles. Use inside the PanelRouter route tree.
 *
 * Props:
 *   panels         {string[]}  User's panel must be one of these.
 *   globalRoles    {string[]}  Alternative override: match on globalRole instead.
 *   communityRoles {string[]}  Require the user's communityRole in activeTenant to be one of these.
 *   module         {string}    Optional: module key from Tenant.moduleConfig.
 *                              If provided, also checks isModuleEnabled(module).
 *                              cip_admin and cip_staff bypass this check.
 *   fallback       {string}    Redirect destination on failure. Default: '/dashboard'.
 *   children       {node}      Page component.
 */
export const PanelRoute = ({ panels, globalRoles, communityRoles, module: moduleName, fallback = '/dashboard', children }) => {
  const { user, panel, access, activeTenant, isModuleEnabled } = useAuth();

  if (!user) return <Navigate to="/login" replace />;

  const panelOk         = !panels         || panels.includes(panel);
  const globalRoleOk    = !globalRoles    || globalRoles.includes(user.globalRole);

  // communityRoles checks the user's role in their activeTenant
  let communityRoleOk = true;
  if (communityRoles) {
    const roleInCommunity = activeTenant ? access?.communityRoles?.[activeTenant._id] : null;
    communityRoleOk = communityRoles.includes(roleInCommunity);
  }

  if ((!panelOk && !globalRoleOk) || !communityRoleOk) {
    return <Navigate to={fallback} replace />;
  }

  // Module check: bypass for platform staff/admin; enforce for community users
  if (moduleName && !isModuleEnabled(moduleName)) {
    const isPlatformRole = user.globalRole === 'cip_admin' || user.globalRole === 'cip_staff';
    if (!isPlatformRole) {
      return <Navigate to={fallback} replace />;
    }
  }

  return children;
};

export default PanelRouter;
